import { Directive, OnInit, inject } from '@angular/core';
import { IProductData, Product } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { Basket } from '@df/session/api/basket/basket';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import isEqual from 'lodash-es/isEqual';
import maxBy from 'lodash-es/maxBy';
import { AdvancedCommerce } from '../advanced-commerce.namespace';
import { AdvancedCommerceService } from '../advanced-commerce.service';

let uid = 0;

export const advancedCommerceRecommendationsUi = () => {
    return `advanced-commerce-recommendations-${uid++}`;
};

@Directive()
export class AdvancedCommerceRecommendationsComponentDef<T extends AdvancedCommerce.Recommendations.ComponentData>
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit
{
    @ICmsContent({
        type: 'component'
    })
    component?: T;

    protected advancedCommerceService = inject(AdvancedCommerceService);
    protected meta = inject(Meta);
    protected basket = inject(Basket);
    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    protected toggleService = inject(ToggleService);

    sections: AdvancedCommerce.Recommendations.Sections[] = [];
    printedSectionIndexes: number[] = [];
    readonly uid = advancedCommerceRecommendationsUi();
    protected debouncedCmsFieldChange!: () => void;
    private productsIds: number[] = [];
    protected previousId?: number;

    override ngOnInit(): void {
        super.ngOnInit();

        this.debouncedCmsFieldChange = this.timeoutService.debounce(() => this.onCmsFieldChange(), 500);

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (
                    event.changeType === NICmsDataChange.EChangeType.change &&
                    this.cmsObservedData.some(path => event.valuePath.includes(path))
                ) {
                    this.debouncedCmsFieldChange();
                }
            })
        );

        this.subscriptions.add(
            this.meta.subject.subscribe(metaState => {
                // observe product change and load new products on parent ID change
                if (metaState?.model?.type === 'product') {
                    const model = metaState.model as Product<IProductData>;

                    if (this.previousId !== model.id) {
                        this.previousId = model.id;

                        this.debouncedCmsFieldChange();
                    }
                }
            })
        );

        this.subscriptions.add(
            this.basket.subscribe(() => {
                const newProductsIds = this.getProductIds();

                if (!isEqual(newProductsIds, this.productsIds)) {
                    this.debouncedCmsFieldChange();
                }
            })
        );

        this.debouncedCmsFieldChange();
    }

    get cmsObservedData(): string[] {
        return ['sections', 'identifier', 'source', 'limit'];
    }

    /**
     * If component uses multiple sections
     */
    get hasManySections(): boolean {
        return this.printedSectionIndexes.length > 1;
    }

    get printedSections(): AdvancedCommerce.Recommendations.Section[] {
        return this.component?.sections.filter((section, index) => this.printedSectionIndexes.includes(index)) || [];
    }

    get groupName(): string {
        return this.uid;
    }

    /**
     * Method used by related-products component,
     * sets section related to it as printed (allowed to be displayed)
     * if related-products component found relevant products to display.
     * We do not display section if it does not have any products to present.
     *
     * isPrinted is used to prevent max stack directive error, by default true
     * cause not all projects require it, easy to forget to pass it
     */
    printSection(index: number, isPrinted: boolean = true): void {
        if (!isPrinted || index < 0) {
            return;
        }

        if (!this.printedSectionIndexes.includes(index)) {
            this.printedSectionIndexes.push(index);

            // Open section if it is not open or if current open section is after printed index
            const currentOpenIndex = this.toggleService.getGroup(this.groupName).current.value?.id[1];
            if (!this.toggleService.getGroup(this.groupName).isOpen || (typeof currentOpenIndex === 'number' && currentOpenIndex > index)) {
                this.toggleService.open([this.groupName, index]);
            }

            this.detectChanges();
        }
    }

    isSectionPrinted(i: number): boolean {
        return this.printedSectionIndexes?.indexOf(i) !== -1;
    }

    protected async onCmsFieldChange(): Promise<void> {
        const categoryId = this.getCategoryId();
        const options: AdvancedCommerce.Engine.Recommendations.Options = {
            elements: this.getElements()
        };

        if (categoryId) {
            options.context = {
                category_id: `${categoryId}`
            };
        }

        this.productsIds = this.getProductIds();

        if (this.canGetData() && (this.productsIds.length || this.component?.source !== AdvancedCommerce.Recommendations.Source.basket)) {
            const data = await this.advancedCommerceService.getRecommendations<AdvancedCommerce.Query.Response>(
                options,
                this.productsIds,
                true
            );

            if (data.elements) {
                this.sections = [];

                Object.keys(data.elements).forEach(key => {
                    const element = data.elements[key];
                    const ids = element.products.results.map(result => Number(result.item.id));

                    if (ids?.length && !this.sections.find(section => section.identifier === key)) {
                        const title = this.component?.sections.find(section => section.identifier === key)?.title || key;

                        this.sections.push({
                            identifier: key,
                            title: element.titles?.default || title,
                            clickCode: element.products?.meta?.click_code,
                            ids: element.products.results.map(result => Number(result.item.id))
                        });
                    }
                });

                this.detectChanges();
            }
        }
    }

    protected canGetData(): boolean {
        return true;
    }

    protected getCategoryId(): string | number | undefined {
        const previousModel = this.meta.previousState?.model;

        if (previousModel?.type === 'category') {
            return previousModel.id;
        }

        return undefined;
    }

    protected getElements(): string[] {
        return this.component?.sections.map(section => section.identifier) || [];
    }

    protected getProductIds(): number[] {
        switch (this.component?.source) {
            case AdvancedCommerce.Recommendations.Source.basket:
                if (this.basket.items?.length > 0) {
                    const mostExpensiveProduct = maxBy(
                        this.basket.items.map(item => item.product),
                        'price'
                    );
                    const id = mostExpensiveProduct?.parent?.id || mostExpensiveProduct?.id;
                    return id ? [id] : [];
                }

                return [];

            case AdvancedCommerce.Recommendations.Source.minibasket:
                if (this.basket.items?.length > 0) {
                    const latestProductProduct = maxBy(this.basket.items, i => i?.data?.['added_at']);
                    const id = latestProductProduct?.product?.parent?.id || latestProductProduct?.product?.id;
                    return id ? [id] : [];
                }

                return [];

            case AdvancedCommerce.Recommendations.Source.product:
                if (this.meta.model?.type === 'product') {
                    return [Number((this.meta.model as Product<IProductData>)?.parent?.id || this.meta.model?.id)];
                }

                return [];

            default:
                return [];
        }
    }
}
